import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getBackendUrl } from "@/config/admin";
import { AUTH_UNAUTHORIZED_EVENT, apiFetch, apiJson, normalizePath, type ApiError } from "@/lib/api";
import { API_AUTH, getOtpRequestErrorMessage } from "@/lib/api-paths";
import { AUTH_TOKEN_KEY, logoutAdmin } from "@/lib/admin";
import { isMemberAppPath } from "@/lib/routes";
import { roleKeyToPanelRole } from "@/lib/panel-roles";

const TOKEN_KEY = AUTH_TOKEN_KEY;

export type PanelRole = "superadmin" | "admin" | "tech_admin" | "manager" | "user";

/** effectiveAccess — shared contract between web and app (backend source of truth). */
export interface EffectiveAccess {
  roleKey: string;
  hierarchyLevel: number;
  permissions: string[];
  modules: string[];
  /** Admin-panel modules only; use for /admin and admin sidebar so USER (member-only) cannot access. */
  adminModules?: string[];
  features: Record<string, boolean>;
  version: string;
}

/** Backend GET /auth/me response. Includes effectiveAccess for permission-driven UI. */
interface BackendMeResponse {
  userId: string;
  email: string;
  name?: string;
  mobile?: string;
  baseRole: string;
  canAccessSuperPanel?: boolean;
  permissions?: string[];
  roles?: string[];
  admin?: boolean;
  status?: string;
  memberStatus?: string | null;
  pushNotificationsEnabled?: boolean;
  effectiveAccess?: EffectiveAccess;
}

function mapBackendMeToAuthUser(me: BackendMeResponse, fallbackEmail?: string): AuthUser {
  const email = me.email || fallbackEmail || "";
  const role: PanelRole =
    me.effectiveAccess?.roleKey != null && me.effectiveAccess.roleKey !== ""
      ? roleKeyToPanelRole(me.effectiveAccess.roleKey)
      : me.canAccessSuperPanel
        ? "superadmin"
        : me.baseRole?.toUpperCase() === "TECH_ADMIN"
          ? "tech_admin"
          : me.baseRole?.toUpperCase() === "ADMIN"
            ? "admin"
            : me.baseRole?.toUpperCase() === "MANAGER"
              ? "manager"
              : "user";
  const id = parseInt(me.userId, 10) || 0;
  const name = (me.name && me.name.trim()) || (email ? email.split("@")[0].replace(/[._]/g, " ") || "User" : "User");
  return { id: isNaN(id) ? 0 : id, name, email, role, mobile: me.mobile ?? undefined };
}

function getLoginErrorMessage(apiErr: ApiError): string {
  const msg = apiErr.message ?? "";
  if (msg.includes("Failed to fetch") || msg.includes("NetworkError") || msg.includes("Load failed")) {
    return "Cannot reach the server. If you're in the app, ensure the backend URL is correct and reachable (on a physical device use your computer's IP, not localhost).";
  }
  const body = apiErr.body as { error?: string; message?: string | string[] } | undefined;
  if (body?.message) {
    const m = Array.isArray(body.message) ? body.message.join(". ") : body.message;
    if (m) return m;
  }
  if (body?.error && typeof body.error === "string") return body.error;
  if (apiErr.status === 400) return "Invalid request. Please enter a valid email address.";
  if (apiErr.status === 401) return apiErr.message || "Invalid credentials.";
  if (apiErr.status === 403) return apiErr.message || "Account is suspended. Please contact support.";
  return apiErr.message || "Login failed. Please try again.";
}

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: PanelRole;
  mobile?: string;
}

interface AuthState {
  user: AuthUser | null;
  permissions: string[];
  effectiveAccess: EffectiveAccess | null;
  /** User.status from /auth/me (ACTIVE, SUSPENDED, TRASHED). */
  status: string | null;
  /** MemberProfile.memberStatus from /auth/me (DRAFT, SUBMITTED, VERIFIED, APPROVED, ACTIVE, etc.). */
  memberStatus: string | null;
  pushNotificationsEnabled: boolean;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, _password?: string) => Promise<{ ok: boolean; user?: AuthUser; error?: string }>;
  /** Request OTP for email (no token returned). Use context 'LOGIN' on login page so backend returns 404 if user does not exist. */
  requestOtp: (email: string, context?: 'LOGIN' | 'REGISTER') => Promise<{ ok: boolean; error?: string }>;
  /** Verify OTP; on success sets token and user. */
  verifyOtp: (email: string, otp: string) => Promise<{ ok: boolean; user?: AuthUser; error?: string }>;
  logout: () => void;
  /** Check permission (alias: use effectiveAccess.permissions when available). */
  hasPermission: (code: string) => boolean;
  can: (permission: string) => boolean;
  hasModule: (moduleKey: string) => boolean;
  /** True only if moduleKey is in effectiveAccess.adminModules (use for admin panel/sidebar). */
  hasAdminModule: (moduleKey: string) => boolean;
  isFeatureEnabled: (flag: string) => boolean;
  refreshSession: () => Promise<void>;
  updateNotificationPreferences: (enabled: boolean) => Promise<void>;
  /** True if user or member is suspended (for suspended banner). */
  isSuspended: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setStoredToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [effectiveAccess, setEffectiveAccess] = useState<EffectiveAccess | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [memberStatus, setMemberStatus] = useState<string | null>(null);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(true);
  const [token, setTokenState] = useState<string | null>(() => getStoredToken());
  const [loading, setLoading] = useState(true);

  const setToken = useCallback((value: string | null) => {
    setTokenState(value);
    setStoredToken(value);
  }, []);

  const refreshSession = useCallback(async () => {
    const t = getStoredToken();
    if (!t) {
      setUser(null);
      setPermissions([]);
      setEffectiveAccess(null);
      setStatus(null);
      setMemberStatus(null);
      setTokenState(null);
      setLoading(false);
      return;
    }
    try {
      const data = await apiJson<BackendMeResponse>(API_AUTH.me, { cache: "no-store" });
      setUser(mapBackendMeToAuthUser(data));
      setPermissions(data.permissions ?? data.effectiveAccess?.permissions ?? []);
      setEffectiveAccess(data.effectiveAccess ?? null);
      setStatus(data.status ?? null);
      setMemberStatus(data.memberStatus ?? null);
      setPushNotificationsEnabled(data.pushNotificationsEnabled ?? true);
      setTokenState(t);
      queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
      const adminModules = data.effectiveAccess?.adminModules;
      if (adminModules?.length && isMemberAppPath(window.location.pathname)) {
        window.location.replace("/admin");
      }
    } catch {
      // On 401, try refresh token (cookie-based) before giving up
      try {
        const refreshRes = await apiFetch(API_AUTH.refresh, {
          method: "POST",
          skipAuth: true,
          credentials: "include",
        });
        if (refreshRes.ok) {
          const raw = await refreshRes.json().catch(() => ({})) as { data?: { accessToken?: string }; accessToken?: string };
          const newToken = raw.data?.accessToken ?? raw.accessToken;
          if (newToken) {
            setStoredToken(newToken);
            setTokenState(newToken);
            const meData = await apiJson<BackendMeResponse>(API_AUTH.me, { cache: "no-store" });
            setUser(mapBackendMeToAuthUser(meData));
            setPermissions(meData.permissions ?? meData.effectiveAccess?.permissions ?? []);
            setEffectiveAccess(meData.effectiveAccess ?? null);
            setStatus(meData.status ?? null);
            setMemberStatus(meData.memberStatus ?? null);
            setPushNotificationsEnabled(meData.pushNotificationsEnabled ?? true);
            setLoading(false);
            queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
            const adminModules = meData.effectiveAccess?.adminModules;
            if (adminModules?.length && isMemberAppPath(window.location.pathname)) {
              window.location.replace("/admin");
            }
            return;
          }
        }
      } catch {
        // Refresh failed; fall through to clear auth
      }
      setUser(null);
      setPermissions([]);
      setEffectiveAccess(null);
      setStatus(null);
      setMemberStatus(null);
      setPushNotificationsEnabled(true);
      setStoredToken(null);
      setTokenState(null);
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  // Ref for SSE connection so logout can close it and avoid orphaned connection
  const sseEventSourceRef = useRef<EventSource | null>(null);

  // When user returns to tab, refresh session so permissions stay in sync after Super Admin changes.
  const lastRefreshOnFocusRef = useRef<number>(0);
  const REFRESH_ON_FOCUS_THROTTLE_MS = 5_000; // at most once per 5 seconds so sidebar updates sooner when refocusing tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!getStoredToken()) return;
      const now = Date.now();
      if (now - lastRefreshOnFocusRef.current < REFRESH_ON_FOCUS_THROTTLE_MS) return;
      lastRefreshOnFocusRef.current = now;
      refreshSession();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refreshSession]);

  // SSE: real-time permission push via EventSource. When the server sends
  // "permissions-changed", we do a full refreshSession() so the UI updates instantly.
  // Backs off more aggressively on 5xx and caps reconnect attempts.
  const MAX_SSE_RECONNECT_ATTEMPTS = 10;
  const INITIAL_SSE_DELAY = 3000;
  const DELAY_AFTER_SSE_5XX = 15_000;
  const MAX_SSE_DELAY = 60000;
  const sseReconnectAttemptsRef = useRef(0);
  const lastSseToken5xxRef = useRef(false);

  useEffect(() => {
    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    sseReconnectAttemptsRef.current = 0;
    lastSseToken5xxRef.current = false;
    let reconnectDelay = INITIAL_SSE_DELAY;

    const connect = async () => {
      const t = getStoredToken();
      if (!t) return;
      if (sseReconnectAttemptsRef.current >= MAX_SSE_RECONNECT_ATTEMPTS) return;

      const base = getBackendUrl();
      const normalized = normalizePath(API_AUTH.permissionsStream, base);
      const pathPart = normalized.startsWith("http") ? normalized : normalized.replace(/^\//, "") || "auth/permissions-stream";
      let sseParam = `token=${encodeURIComponent(t)}`;
      try {
        const { sseToken } = await apiJson<{ sseToken: string }>(API_AUTH.sseToken, {
          method: "POST",
          retry: 5,
        });
        if (sseToken) {
          sseParam = `sseToken=${encodeURIComponent(sseToken)}`;
          lastSseToken5xxRef.current = false;
        }
      } catch (e) {
        const status = (e as { status?: number })?.status;
        if (status != null && status >= 500) {
          lastSseToken5xxRef.current = true;
        }
        /* fall back to legacy JWT param */
      }
      const url = pathPart.startsWith("http")
        ? `${pathPart}?${sseParam}`
        : `${base.replace(/\/+$/, "")}/${pathPart}?${sseParam}`;
      es = new EventSource(url);
      sseEventSourceRef.current = es;
      es.onopen = () => {
        reconnectDelay = INITIAL_SSE_DELAY;
        sseReconnectAttemptsRef.current = 0;
      };
      es.onmessage = (event) => {
        try {
          const raw = event.data;
          const payload = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (payload?.type === "connected") {
            reconnectDelay = INITIAL_SSE_DELAY;
          }
          if (payload?.type === "permissions-changed") {
            if (import.meta.env.DEV) console.log("[SSE] permissions-changed received, refreshing session");
            refreshSession().then(() => {
              if (import.meta.env.DEV) console.log("[SSE] refreshSession completed");
            });
          }
        } catch { /* ignore parse errors */ }
      };
      es.onerror = () => {
        es?.close();
        es = null;
        sseReconnectAttemptsRef.current += 1;
        if (sseReconnectAttemptsRef.current >= MAX_SSE_RECONNECT_ATTEMPTS) return;
        const baseDelay = lastSseToken5xxRef.current ? DELAY_AFTER_SSE_5XX : reconnectDelay;
        const jitter = 0.5 + Math.random() * 0.5;
        const delay = Math.min(baseDelay * jitter, MAX_SSE_DELAY);
        reconnectDelay = Math.min(reconnectDelay * 2, MAX_SSE_DELAY);
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      es?.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [refreshSession, token]);

  // When API client gets 401 it clears token and dispatches this; we sync context.
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setPermissions([]);
      setEffectiveAccess(null);
      setStatus(null);
      setMemberStatus(null);
      setPushNotificationsEnabled(true);
      setTokenState(null);
      setStoredToken(null);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  const login = useCallback(
    async (_email: string, _password?: string): Promise<{ ok: boolean; user?: AuthUser; error?: string }> => {
      return { ok: false, error: "Use Request OTP and Verify OTP to log in." };
    },
    []
  );

  const requestOtp = useCallback(async (email: string, context?: 'LOGIN' | 'REGISTER'): Promise<{ ok: boolean; error?: string }> => {
    try {
      await apiJson<{ message?: string }>(API_AUTH.requestOtp, {
        method: "POST",
        skipAuth: true,
        body: { email: email.trim().toLowerCase(), ...(context && { context }) },
      });
      return { ok: true };
    } catch (e) {
      const err = e as ApiError & { message?: string; status?: number; body?: unknown };
      const message = getOtpRequestErrorMessage(err, "Could not send OTP. Please try again.");
      return { ok: false, error: message };
    }
  }, []);

  const verifyOtp = useCallback(
    async (email: string, otp: string): Promise<{ ok: boolean; user?: AuthUser; error?: string }> => {
      try {
        const data = await apiJson<{
          accessToken: string;
          userId?: string;
        }>(API_AUTH.verifyOtp, {
          method: "POST",
          skipAuth: true,
          credentials: "include",
          body: { email: email.trim().toLowerCase(), otp: otp.trim() },
        });
        const t = data.accessToken;
        if (!t) {
          return { ok: false, error: "No token received" };
        }
        setToken(t);
        const meData = await apiJson<BackendMeResponse>(API_AUTH.me, { cache: "no-store" });
        const authUser = mapBackendMeToAuthUser(meData, email);
        setUser(authUser);
        setPermissions(meData.permissions ?? meData.effectiveAccess?.permissions ?? []);
        setEffectiveAccess(meData.effectiveAccess ?? null);
        setStatus(meData.status ?? null);
        setMemberStatus(meData.memberStatus ?? null);
        setPushNotificationsEnabled(meData.pushNotificationsEnabled ?? true);
        return { ok: true, user: authUser };
      } catch (e) {
        const err = e as ApiError & { message?: string; status?: number; body?: { message?: string } };
        const status = err?.status;
        const bodyMsg = typeof err?.body?.message === "string" ? err.body.message : "";
        if (status === 404 || bodyMsg.includes("User does not exist") || bodyMsg.includes("does not exist")) {
          return { ok: false, error: "User doesn't exist. Please register first." };
        }
        if (status === 401) {
          return {
            ok: false,
            error: "Invalid or expired OTP. Please request a new one.",
          };
        }
        if (status === 429) {
          return {
            ok: false,
            error: "Too many attempts. Please try again later.",
          };
        }
        const msg = err?.message ?? String(e);
        if (msg.includes("Failed to fetch") || msg.includes("NetworkError") || msg.includes("Load failed")) {
          return {
            ok: false,
            error: "Cannot reach the server. Check your connection and backend URL.",
          };
        }
        const message = getLoginErrorMessage(err as ApiError);
        return { ok: false, error: message };
      }
    },
    [setToken]
  );

  const logout = useCallback(() => {
    // Close SSE connection so it is not left open after logout
    if (sseEventSourceRef.current) {
      sseEventSourceRef.current.close();
      sseEventSourceRef.current = null;
    }
    // Notify backend to blacklist token and revoke refresh token (fire-and-forget)
    const token = getStoredToken();
    if (token) {
      apiFetch(API_AUTH.logout, { method: "POST", credentials: "include" }).catch((err) => {
        if (import.meta.env.DEV) console.warn("Logout API call failed", err);
      });
    }
    setUser(null);
    setPermissions([]);
    setEffectiveAccess(null);
    setStatus(null);
    setMemberStatus(null);
    setToken(null);
    logoutAdmin();
  }, [setToken]);

  const hasPermission = useCallback(
    (code: string): boolean => {
      if (user?.role === "superadmin") return true;
      if (effectiveAccess?.permissions?.includes(code)) return true;
      return permissions.includes(code);
    },
    [user?.role, effectiveAccess?.permissions, permissions]
  );

  const can = useCallback((permission: string) => hasPermission(permission), [hasPermission]);

  const hasModule = useCallback(
    (moduleKey: string): boolean => {
      return effectiveAccess?.modules?.includes(moduleKey) ?? false;
    },
    [effectiveAccess?.modules]
  );

  const hasAdminModule = useCallback(
    (moduleKey: string): boolean => {
      return effectiveAccess?.adminModules?.includes(moduleKey) ?? false;
    },
    [effectiveAccess?.adminModules]
  );

  const isFeatureEnabled = useCallback(
    (flag: string): boolean => {
      return effectiveAccess?.features?.[flag] === true;
    },
    [effectiveAccess?.features]
  );

  const updateNotificationPreferences = useCallback(async (enabled: boolean) => {
    try {
      await apiJson(API_AUTH.notificationPreferences, {
        method: "PATCH",
        body: { pushNotificationsEnabled: enabled },
      });
      setPushNotificationsEnabled(enabled);
    } catch {
      toast.error("Failed to update notification preferences. Please try again.");
    }
  }, []);

  const isSuspended = status === "SUSPENDED" || memberStatus === "SUSPENDED";

  const value: AuthContextValue = {
    user,
    permissions,
    effectiveAccess,
    status,
    memberStatus,
    pushNotificationsEnabled,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    requestOtp,
    verifyOtp,
    logout,
    hasPermission,
    can,
    hasModule,
    hasAdminModule,
    isFeatureEnabled,
    refreshSession,
    updateNotificationPreferences,
    isSuspended,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    const msg =
      "useAuth must be used within AuthProvider. " +
      "If you see this after an error, try reloading the page. " +
      "Ensure AuthProvider wraps all routes in App.";
    throw new Error(msg);
  }
  return ctx;
}

export function getAuthToken(): string | null {
  return getStoredToken();
}

export function isAuthLoggedIn(): boolean {
  return !!getStoredToken();
}
